import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';
import { productGroups } from '../../data/products';
import { doorFamilies } from '../../data/configurator';
import { articles } from '../../data/articles';
import { useLanguage } from '../../i18n/LanguageContext';
import { trackEvent } from '../../analytics';
import { getFromPrice } from '../../services/pricingService';
import { SHOW_CONFIGURATOR_LINKS } from '../../config/features';

const BASE_URL = 'https://www.hestiadoors.com';

export const allModels = productGroups.flatMap((group) =>
  group.models.map((model: any) => ({ model, group }))
);
export const catalogModels = allModels.filter(
  ({ model, group }: any) => model.catalogVisible !== false && group.catalogVisible !== false
);

const ConfigurationDiagram = ({ type }: { type: string }) => {
  const panels = type === 'double'
    ? ['1fr', '1fr']
    : type === 'sidelight'
      ? ['0.38fr', '1fr', '0.38fr']
      : ['1fr'];

  return (
    <div
      aria-hidden="true"
      style={{ display: 'grid', gridTemplateColumns: panels.join(' '), gap: '3px', width: '72px', height: '62px', marginBottom: '0.75rem' }}
    >
      {panels.map((_, index) => (
        <span
          key={index}
          style={{
            border: '2px solid #dfa926',
            background: type === 'sidelight' && index !== 1 ? 'rgba(223, 169, 38, 0.12)' : 'rgba(58, 58, 58, 0.4)',
            borderRadius: '2px'
          }}
        />
      ))}
    </div>
  );
};

const ProductPage = () => {
  const { slug } = useParams();
  const { t, pick, path, lang } = useLanguage();

  const entry = allModels.find((e: any) => e.model.slug === slug);
  const cfgFamily = entry ? doorFamilies.find((f: any) => f.slug === entry.model.slug) : undefined;
  const [fromPrice, setFromPrice] = useState<number | null>(null);

  useEffect(() => {
    if (entry) trackEvent('product_view', { product: slug });
    setFromPrice(null);
    if (cfgFamily) getFromPrice(cfgFamily).then(setFromPrice);
  }, [slug, entry, cfgFamily]);

  const pageStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #2d2d2d 100%)',
    paddingTop: '4rem'
  };

  const containerStyle = {
    maxWidth: '72rem',
    margin: '0 auto',
    padding: '2rem 1.5rem'
  };

  const highlightStyle = { color: '#dfa926' };

  if (!entry) {
    return (
      <div style={pageStyle}>
        <SEO
          title={`${t('productPage.notFound')} | Hestia Doors`}
          description={t('productPage.notFoundDesc')}
          noindex
        />
        <Navigation />
        <div style={{ ...containerStyle, textAlign: 'center' as const, padding: '6rem 1.5rem' }}>
          <h1 style={{ fontSize: '3rem', fontWeight: 'bold', color: 'white', marginBottom: '1rem' }}>
            {t('productPage.notFound')}
          </h1>
          <p style={{ color: '#9ca3af', marginBottom: '2rem' }}>{t('productPage.notFoundDesc')}</p>
          <Link to={path('/products')} style={{ color: '#dfa926', fontWeight: 600 }}>
            {t('productPage.backToProducts')}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const { model, group } = entry as any;
  const name = pick(model, 'name');
  const groupName = pick(group, 'name');
  const description = pick(model, 'description');
  const keyFeatures = pick(model, 'keyFeatures') as string[];
  const specs = pick(model, 'specs') as { label: string; value: string }[];
  const opening = pick(model, 'opening');

  // Descriptive per-product title (model.seoTitle) — falls back to name + group.
  const seoTitle = `${model.seoTitle ? pick(model, 'seoTitle') : `${name} — ${groupName}`} | Hestia Doors`;
  const configurationNames = (model.configurations || []).map((configuration: any) => pick(configuration, 'name'));
  const seoDesc = `${description} ${keyFeatures.slice(0, 2).join('; ')}.${configurationNames.length ? ` ${t('productPage.availableToOrder')}: ${configurationNames.join(', ')}.` : ''}`;

  const productLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Product',
        name,
        description,
        image: `${BASE_URL}${model.image}`,
        sku: model.code,
        material: 'Wood',
        brand: { '@type': 'Brand', name: 'Hestia Doors' },
        manufacturer: {
          '@type': 'Organization',
          name: 'Hestia Doors OÜ',
          url: BASE_URL,
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Päikese 1',
            addressLocality: 'Kohtla-Järve',
            postalCode: '30221',
            addressCountry: 'EE'
          }
        },
        additionalProperty: [
          ...specs.map((s) => ({
            '@type': 'PropertyValue',
            name: s.label,
            value: s.value
          })),
          ...(configurationNames.length ? [{
            '@type': 'PropertyValue',
            name: t('productPage.configurations'),
            value: configurationNames.join(', ')
          }] : [])
        ]
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t('productPage.breadcrumbHome'), item: `${BASE_URL}${lang === 'et' ? '/et' : '/'}` },
          { '@type': 'ListItem', position: 2, name: t('productPage.breadcrumbProducts'), item: `${BASE_URL}${lang === 'et' ? '/et/products' : '/products'}` },
          { '@type': 'ListItem', position: 3, name }
        ]
      }
    ]
  };

  const related = catalogModels.filter((e: any) => e.model.slug !== slug);
  const relatedArticle = articles.find(
    (a: any) => a.published && (a.productSlugs || []).includes(slug)
  );

  return (
    <div style={pageStyle}>
      <SEO
        title={seoTitle}
        description={seoDesc}
        canonicalPath={`/products/${slug}`}
        keywords={`${name}, ${pick(group, 'name')}, exterior doors, välisuksed`}
        jsonLd={productLd}
        ogType="product"
        ogImage={model.image}
      />
      <Navigation />

      <div style={containerStyle}>
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" style={{ marginBottom: '2rem', fontSize: '0.875rem' }}>
          <ol style={{ listStyle: 'none', display: 'flex', gap: '0.5rem', padding: 0, margin: 0, flexWrap: 'wrap' as const }}>
            <li><Link to={path('/')} style={{ color: '#9ca3af', textDecoration: 'none' }}>{t('productPage.breadcrumbHome')}</Link></li>
            <li style={{ color: '#6b7280' }}>/</li>
            <li><Link to={path('/products')} style={{ color: '#9ca3af', textDecoration: 'none' }}>{t('productPage.breadcrumbProducts')}</Link></li>
            <li style={{ color: '#6b7280' }}>/</li>
            <li style={{ color: '#dfa926' }} aria-current="page">{name}</li>
          </ol>
        </nav>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: '3rem', marginBottom: '3rem' }}>
          <div>
            <span style={{ display: 'inline-block', backgroundColor: 'rgba(223, 169, 38, 0.15)', color: '#dfa926', padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 500, marginBottom: '0.75rem' }}>
              {groupName}
            </span>
            <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'white', margin: '0 0 0.25rem 0' }}>
              {name}
            </h1>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
              {t('productPage.modelCode')}: {model.code} · {t('productPage.opening')}: {opening}
            </p>
            {cfgFamily && fromPrice != null && (
              <p style={{ color: '#dfa926', fontSize: '1.1rem', fontWeight: 700, margin: '-0.5rem 0 1rem' }}>
                {t('productPage.fromPrice')} €{fromPrice}
              </p>
            )}
            <p style={{ color: '#d1d5db', lineHeight: 1.7, marginBottom: '1.5rem' }}>{description}</p>

            <h2 style={{ ...highlightStyle, fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem' }}>
              {t('productPage.keyFeatures')}
            </h2>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 2rem 0' }}>
              {keyFeatures.map((f: string, i: number) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.5rem', color: '#d1d5db' }}>
                  <span style={{ width: '0.5rem', height: '0.5rem', backgroundColor: '#dfa926', borderRadius: '50%', marginTop: '0.4rem', marginRight: '0.75rem', flexShrink: 0 }} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            {model.configurations && model.configurations.length > 0 && (
              <section style={{ marginBottom: '2rem' }}>
                <h2 style={{ ...highlightStyle, fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  {t('productPage.configurations')}
                </h2>
                <p style={{ color: '#9ca3af', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1rem' }}>
                  {t('productPage.configurationsIntro')}
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                  {model.configurations.map((configuration: any) => (
                    <div
                      key={configuration.id}
                      style={{
                        background: 'rgba(58, 58, 58, 0.3)',
                        border: '1px solid rgba(223, 169, 38, 0.25)',
                        borderRadius: '0.5rem',
                        padding: '1rem'
                      }}
                    >
                      <ConfigurationDiagram type={configuration.id} />
                      <strong style={{ color: 'white', display: 'block', marginBottom: '0.35rem' }}>
                        {pick(configuration, 'name')}
                      </strong>
                      {configuration.id !== 'single' && (
                        <span style={{ color: '#dfa926', fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.35rem' }}>
                          {t('productPage.availableToOrder')}
                        </span>
                      )}
                      <span style={{ color: '#9ca3af', fontSize: '0.8rem', lineHeight: 1.5 }}>
                        {pick(configuration, 'description')}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Ordering happens only through the configurator (when listed). */}
            {SHOW_CONFIGURATOR_LINKS && cfgFamily && (
              <p style={{ color: '#9ca3af', fontSize: '0.9rem', lineHeight: 1.6, margin: '0 0 0.9rem' }}>
                {t('productPage.configureOrderHint')}
              </p>
            )}
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' as const }}>
              {SHOW_CONFIGURATOR_LINKS && cfgFamily && (
                <Link
                  to={path(`/configurator?family=${cfgFamily.id}`)}
                  onClick={() => trackEvent('product_configure_click', { product: model.slug })}
                  style={{ backgroundColor: '#dfa926', color: 'black', fontWeight: 600, padding: '0.85rem 1.75rem', borderRadius: '0.5rem', textDecoration: 'none' }}
                >
                  {t('productPage.configureOrder')}
                </Link>
              )}
              <Link
                to={path('/contact')}
                onClick={() => trackEvent('product_enquiry', { product: model.slug })}
                style={SHOW_CONFIGURATOR_LINKS
                  ? { border: '2px solid #dfa926', color: '#dfa926', fontWeight: 600, padding: '0.85rem 1.75rem', borderRadius: '0.5rem', textDecoration: 'none' }
                  : { backgroundColor: '#dfa926', color: 'black', fontWeight: 600, padding: '0.85rem 1.75rem', borderRadius: '0.5rem', textDecoration: 'none' }}
              >
                {t('productPage.requestQuote')}
              </Link>
              <Link
                to={path('/products')}
                style={{ color: '#9ca3af', fontSize: '0.9rem', textDecoration: 'underline', alignSelf: 'center' }}
              >
                {t('productPage.backToProducts')}
              </Link>
            </div>
          </div>

          <div>
            <img
              src={model.image}
              alt={`${name} — ${groupName}`}
              style={{ width: '100%', borderRadius: '0.75rem', background: 'linear-gradient(45deg, #3a3a3a, #1a1a1a)', display: 'block' }}
            />
          </div>
        </div>

        {/* Technical specifications */}
        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{ ...highlightStyle, fontSize: '1.5rem', fontWeight: 600, marginBottom: '1.25rem' }}>
            {t('productPage.specs')}
          </h2>
          <dl style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '0.75rem 2rem',
            background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.5) 0%, rgba(17, 17, 17, 0.5) 100%)',
            border: '1px solid rgba(58, 58, 58, 0.5)',
            borderRadius: '0.75rem',
            padding: '1.5rem 2rem',
            margin: 0
          }}>
            {specs.map((s, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column' as const, borderBottom: '1px solid rgba(58, 58, 58, 0.35)', paddingBottom: '0.6rem' }}>
                <dt style={{ color: '#9ca3af', fontSize: '0.8rem', textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>{s.label}</dt>
                <dd style={{ color: 'white', margin: '0.25rem 0 0 0', fontSize: '0.95rem' }}>{s.value}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Related products */}
        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{ ...highlightStyle, fontSize: '1.5rem', fontWeight: 600, marginBottom: '1.25rem' }}>
            {t('productPage.related')}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
            {related.map((e: any) => (
              <Link
                key={e.model.slug}
                to={path(`/products/${e.model.slug}`)}
                style={{
                  background: 'rgba(58, 58, 58, 0.3)',
                  border: '1px solid rgba(75, 85, 99, 0.5)',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  textDecoration: 'none',
                  transition: 'border-color 0.2s'
                }}
              >
                <img
                  src={e.model.image}
                  alt={pick(e.model, 'name')}
                  loading="lazy"
                  style={{ width: '100%', height: '120px', objectFit: 'cover' as const, borderRadius: '0.375rem', marginBottom: '0.75rem', background: 'linear-gradient(45deg, #3a3a3a, #1a1a1a)' }}
                />
                <span style={{ color: '#dfa926', fontWeight: 600, fontSize: '0.9rem' }}>{pick(e.model, 'name')}</span>
                <p style={{ color: '#9ca3af', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>{pick(e.group, 'name')}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* Related guide */}
        {relatedArticle && (
          <section style={{ marginBottom: '3rem' }}>
            <h2 style={{ ...highlightStyle, fontSize: '1.5rem', fontWeight: 600, marginBottom: '1.25rem' }}>
              {t('knowledge.relatedArticles')}
            </h2>
            <Link to={path(`/knowledge/${relatedArticle.slug}`)} style={{ color: '#dfa926', fontWeight: 600 }}>
              {pick(relatedArticle, 'title')}
            </Link>
          </section>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ProductPage;
